import { useState, useEffect } from 'react';
import { fetchWithCache } from '../../../utils/apiCache';
import { Link } from 'react-router-dom';
import RequestQuotePopup from '../../../components/feature/RequestQuotePopup';
import { API_ENDPOINTS } from '../../../config/api';

export default function DraftingServices() {
  const [quoteService, setQuoteService] = useState<string | null>(null);
  const [draftingServices, setDraftingServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await fetchWithCache(API_ENDPOINTS.SERVICES('drafting'));
        setDraftingServices(data);
      } catch (error) {
        console.error("Failed to fetch drafting services", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <>
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-50 text-[#e18033] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-pencil-ruler-2-line"></i>
              <span>12+ Specialized Services</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Drafting Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From architectural plans to MEP systems, we provide complete drafting services for every aspect of your construction project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white dark:bg-slate-800 rounded-xl h-96 shadow-sm w-full"></div>
              ))
            ) : draftingServices.length > 0 ? (
              draftingServices.map((service) => (
                <Link
                  to={`/service/${service.slug}`}
                  key={service.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group block"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg">
                      <i className={`${service.icon} text-2xl text-[#e18033]`}></i>
                    </div>
                    {/* Disclaimer badge with tooltip */}
                    {service.disclaimer && (
                      <div className="absolute bottom-3 right-3 group/disclaimer z-10" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/90 text-white text-[11px] font-semibold rounded-full cursor-help shadow-md backdrop-blur-sm">
                          <i className="ri-error-warning-line text-xs" />
                          {service.disclaimer.label}
                        </div>
                        {/* Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 w-60 px-3 py-2.5 bg-[#212026] text-white text-xs leading-relaxed rounded-xl shadow-xl opacity-0 group-hover/disclaimer:opacity-100 pointer-events-none transition-opacity duration-200 z-20">
                          {service.disclaimer.description}
                          <div className="absolute top-full right-4 w-2.5 h-2.5 bg-[#212026] rotate-45 -mt-1.5" />
                        </div>
                      </div>
                    )}
                  </div>
  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#e18033] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      {service.description}
                    </p>
  
                    <div className="flex flex-wrap gap-2 mb-4">
                      {service.tags && service.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
  
                    <div className="mt-auto pt-4 border-t border-gray-50 flex justify-between items-center">
                      <span className="text-[#e18033] font-semibold text-sm group-hover:text-[#c97029] transition-colors">
                        View Details <i className="ri-arrow-right-line ml-1 transition-transform group-hover:translate-x-1"></i>
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQuoteService(service.title);
                        }}
                        className="inline-flex items-center text-gray-500 hover:text-[#e18033] font-semibold text-sm transition-colors z-10 relative px-3 py-1.5 rounded-md hover:bg-orange-50"
                      >
                        Request Quote
                      </button>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="text-center col-span-3 text-gray-500 py-10">No drafting services found.</p>
            )}
          </div>
        </div>
      </section>

      {/* Request Quote Popup */}
      <RequestQuotePopup
        isOpen={!!quoteService}
        onClose={() => setQuoteService(null)}
        serviceName={quoteService || undefined}
      />
    </>
  );
}
